import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Chip,
  Avatar,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  ListItemText,
  ListItemAvatar,
  Checkbox,
  OutlinedInput,
  SelectChangeEvent,
  Divider,
  IconButton,
  Slide,
  useTheme,
} from '@mui/material';
import { TransitionProps } from '@mui/material/transitions';
import {
  Close as CloseIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Business as BusinessIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import { API_CONFIG } from '../config/api';
import { API_ENDPOINTS } from '../config/apiEndpoints';
import { COLORS } from '../config/colors';

interface SME {
  id: string;
  userId?: string;
  username: string;
  firstName?: string;
  lastName?: string;
  email: string;
  isActive?: boolean;
  expertise?: string;
  availability?: string;
  role?: string;
  originalData?: any;
}

interface AssignSMEDialogProps {
  open: boolean;
  onClose: () => void;
  jd: any;
  onAssign: (smeData: any) => Promise<void>;
}

// Create the transition component with proper typing
const SlideTransition = React.forwardRef<
  unknown,
  TransitionProps & {
    children: React.ReactElement;
  }
>(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    } as React.CSSProperties,
  },
};

const AssignSMEDialog: React.FC<AssignSMEDialogProps> = ({ open, onClose, jd, onAssign }) => {
  const theme = useTheme();
  const [smes, setSmes] = useState<SME[]>([]);
  const [selectedSMEs, setSelectedSMEs] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [assigning, setAssigning] = useState(false);

  const BASE_URL = process.env.REACT_APP_API_BASE_URL;

  // Helper function to get unique ID for SME
  const getSMEId = (sme: any): string => {
    return String(sme.id || sme.userId || sme.user_id || sme.username || `sme-${Math.random()}`);
  };

  // Fetch SMEs from the API
  const fetchSMEs = async () => {
    if (!open) return;

    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem(API_CONFIG.AUTH.TOKEN_KEY);

      if (!token) {
        throw new Error('No authentication token found');
      }

      const response = await fetch(
        `${BASE_URL}${API_ENDPOINTS.USERS.LIST}?page=1&size=100&role=sme`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            accept: 'application/json',
          },
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch SMEs: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      console.log('SMEs API response:', data);

      let smeList = data.users || data.data || data || [];

      if (!Array.isArray(smeList)) {
        console.error('SME list is not an array:', smeList);
        smeList = [];
      }

      // Process and normalize SME data
      const processedSMEs: SME[] = smeList
        .filter((sme: any) => sme.isActive !== false) // Only include active SMEs
        .map((sme: any, index: number) => {
          const uniqueId = getSMEId(sme);

          return {
            id: uniqueId,
            userId: sme.userId || sme.user_id,
            username: sme.username || 'Unknown',
            firstName: sme.firstName || sme.first_name,
            lastName: sme.lastName || sme.last_name,
            email: sme.email || '',
            isActive: sme.isActive !== false,
            expertise: sme.expertise || sme.department || 'General',
            availability: sme.availability || (sme.isActive ? 'Available' : 'Unavailable'),
            role: sme.role || 'sme',
            originalData: sme,
          };
        });

      console.log('Processed SMEs:', processedSMEs);
      setSmes(processedSMEs);

      if (processedSMEs.length === 0) {
        setError('No active SMEs found. Please contact administrator.');
      }
    } catch (err: any) {
      console.error('Error fetching SMEs:', err);
      setError(err.message || 'Failed to load SMEs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      fetchSMEs();
      setSelectedSMEs([]);
    }
  }, [open]);

  // Handle SME selection from dropdown
  const handleSMEChange = (event: SelectChangeEvent<typeof selectedSMEs>) => {
    const value = event.target.value;
    setSelectedSMEs(typeof value === 'string' ? value.split(',') : value);
  };

  const handleAssign = async () => {
    if (selectedSMEs.length === 0) {
      setError('Please select at least one SME');
      return;
    }

    if (!jd?.id && !jd?.jobId) {
      setError('Job ID is missing');
      return;
    }

    setAssigning(true);
    setError(null);

    try {
      const token = localStorage.getItem(API_CONFIG.AUTH.TOKEN_KEY);

      if (!token) {
        throw new Error('No authentication token found');
      }

      // Get selected SME objects
      const selectedSMEObjects = smes.filter(sme => selectedSMEs.includes(sme.id));

      console.log('Assigning SMEs:', selectedSMEObjects);
      console.log('To Job ID:', jd.id || jd.jobId);

      const jobId = jd.id || jd.jobId;

      // Store assignment results for the callback
      const assignmentResults = [];

      // For multiple SMEs, we'll need to call the API for each one
      for (const sme of selectedSMEObjects) {
        const requestPayload = {
          job_id: jobId,
          sme: sme.email || sme.username,
          sme_name: getDisplayName(sme),
        };

        console.log('Assignment request payload:', requestPayload);

        const response = await fetch(`${BASE_URL}${API_ENDPOINTS.JOBS.ASSIGN_SME}`, {
          method: 'PUT',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
            accept: 'application/json',
          },
          body: JSON.stringify(requestPayload),
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(
            errorData.detail ||
              errorData.message ||
              `Assignment failed for ${getDisplayName(sme)}: ${response.status}`
          );
        }

        // Get the response data to verify assignment
        const responseData = await response.json();
        console.log('Assignment response:', responseData);

        // Store the result with SME data for the callback
        assignmentResults.push({
          sme: sme,
          response: responseData,
          assigneeName: getDisplayName(sme),
          assigneeEmail: sme.email || sme.username,
        });
      }

      console.log('Assignment successful for all selected SMEs:', assignmentResults);

      // Call the parent's onAssign callback - let the parent handle data refresh
      if (onAssign) {
        await onAssign({
          assignedSMEs: assignmentResults,
          selectedSMEObjects: selectedSMEObjects,
          jobId: jobId, // Pass the job ID for reference
        });
      }

      // Reset selections and close dialog
      setSelectedSMEs([]);
      onClose();
    } catch (err: any) {
      console.error('Error assigning SMEs:', err);
      setError(err.message || 'Failed to assign SMEs');
    } finally {
      setAssigning(false);
    }
  };

  const getDisplayName = (sme: SME) => {
    if (sme.firstName && sme.lastName) {
      return `${sme.firstName} ${sme.lastName}`;
    }
    return sme.username || 'Unknown';
  };

  const getInitials = (sme: SME) => {
    if (sme.firstName && sme.lastName) {
      return `${sme.firstName[0]}${sme.lastName[0]}`.toUpperCase();
    }
    if (sme.username) {
      return sme.username.substring(0, 2).toUpperCase();
    }
    return 'SM';
  };

  const currentlyAssignedSMEs = jd?.assignedSMEs || [];
  const selectedCount = selectedSMEs.length;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      TransitionComponent={SlideTransition}
      PaperProps={{
        sx: {
          borderRadius: '16px',
          boxShadow: '0 24px 38px 3px rgba(0,0,0,0.14)',
          overflow: 'hidden',
          minHeight: '400px',
          maxHeight: '90vh',
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          background: COLORS.PRIMARY_DARK,
          px: 3,
          py: 3,
          position: 'relative',
          color: 'white',
        }}
      >
        <IconButton
          onClick={onClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: 'white',
            '&:hover': {
              backgroundColor: 'rgba(255,255,255,0.1)',
            },
          }}
        >
          <CloseIcon />
        </IconButton>

        <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
          {currentlyAssignedSMEs.length > 0 ? 'Reassign SME' : 'Assign SME'} to Job Description
        </Typography>

        {jd && (
          <Typography variant="body1" sx={{ opacity: 0.9, fontWeight: 400 }}>
            {jd.id || jd.jobId} • {jd.title || jd.jobTitle || 'Job Description'}
          </Typography>
        )}

        {selectedCount > 0 && (
          <Chip
            label={`${selectedCount} SME${selectedCount > 1 ? 's' : ''} selected`}
            size="small"
            sx={{
              mt: 2,
              backgroundColor: 'rgba(255,255,255,0.2)',
              color: 'white',
              fontWeight: 600,
              fontSize: '0.75rem',
            }}
          />
        )}
      </Box>

      <DialogContent sx={{ p: 3 }}>
        {/* Currently Assigned SMEs */}
        {currentlyAssignedSMEs.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: COLORS.PRIMARY }}>
              Currently Assigned SMEs
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {currentlyAssignedSMEs.map((sme: any, index: number) => (
                <Chip
                  key={getSMEId(sme) || index}
                  avatar={
                    <Avatar sx={{ width: 24, height: 24, fontSize: '0.75rem' }}>
                      {getInitials(sme)}
                    </Avatar>
                  }
                  label={getDisplayName(sme)}
                  color="success"
                  variant="filled"
                  size="medium"
                />
              ))}
            </Box>
            <Divider sx={{ mt: 2, mb: 3 }} />
          </Box>
        )}

        {/* Loading State */}
        {loading && (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 6 }}>
            <CircularProgress size={40} sx={{ mb: 2 }} />
            <Typography variant="body1" color="text.secondary">
              Loading SMEs...
            </Typography>
          </Box>
        )}

        {/* Error State */}
        {error && !loading && (
          <Alert
            severity="error"
            sx={{ mb: 3, borderRadius: '12px' }}
            onClose={() => setError(null)}
          >
            {error}
          </Alert>
        )}

        {/* SME Selection Dropdown */}
        {!loading && !error && smes.length > 0 && (
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: COLORS.PRIMARY }}>
              Select SMEs to Assign
            </Typography>

            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel>Choose SMEs</InputLabel>
              <Select
                multiple
                value={selectedSMEs}
                onChange={handleSMEChange}
                input={<OutlinedInput label="Choose SMEs" />}
                renderValue={selected => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.map(value => {
                      const sme = smes.find(s => s.id === value);
                      return (
                        <Chip
                          key={value}
                          label={sme ? getDisplayName(sme) : value}
                          size="small"
                          sx={{ backgroundColor: COLORS.INFO_LIGHT, color: COLORS.PRIMARY }}
                        />
                      );
                    })}
                  </Box>
                )}
                MenuProps={MenuProps}
                disabled={assigning}
              >
                {smes.map(sme => (
                  <MenuItem key={sme.id} value={sme.id}>
                    <Checkbox checked={selectedSMEs.indexOf(sme.id) > -1} />
                    <ListItemAvatar sx={{ minWidth: 48 }}>
                      <Avatar
                        sx={{
                          width: 32,
                          height: 32,
                          backgroundColor: COLORS.PRIMARY,
                          fontSize: '0.75rem',
                        }}
                      >
                        {getInitials(sme)}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {getDisplayName(sme)}
                        </Typography>
                      }
                      secondary={
                        <Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                            <EmailIcon sx={{ fontSize: 14, color: '#666' }} />
                            <Typography variant="caption">{sme.email}</Typography>
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <BusinessIcon sx={{ fontSize: 14, color: '#666' }} />
                            <Typography variant="caption">
                              {sme.expertise} • {sme.availability}
                            </Typography>
                          </Box>
                        </Box>
                      }
                    />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Selected SMEs Preview */}
            {selectedSMEs.length > 0 && (
              <Box
                sx={{
                  p: 2,
                  border: '1px solid #e0e0e0',
                  borderRadius: 2,
                  backgroundColor: '#f8f9fa',
                }}
              >
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                  SMEs to be assigned ({selectedSMEs.length}):
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {selectedSMEs.map(smeId => {
                    const sme = smes.find(s => s.id === smeId);
                    if (!sme) return null;

                    return (
                      <Chip
                        key={smeId}
                        avatar={
                          <Avatar sx={{ width: 24, height: 24, fontSize: '0.75rem' }}>
                            {getInitials(sme)}
                          </Avatar>
                        }
                        label={getDisplayName(sme)}
                        variant="outlined"
                        color="primary"
                        onDelete={() => {
                          setSelectedSMEs(prev => prev.filter(id => id !== smeId));
                        }}
                      />
                    );
                  })}
                </Box>
              </Box>
            )}
          </Box>
        )}

        {/* Empty State */}
        {!loading && !error && smes.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <PersonIcon sx={{ fontSize: 64, color: '#adb5bd', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
              No SMEs Available
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Please contact administrator to add SMEs to the system.
            </Typography>
          </Box>
        )}
      </DialogContent>

      {/* Actions */}
      <DialogActions
        sx={{
          p: 3,
          backgroundColor: '#f8f9fa',
          borderTop: '1px solid #e0e0e0',
          gap: 2,
        }}
      >
        <Button
          onClick={onClose}
          disabled={assigning}
          variant="outlined"
          sx={{
            textTransform: 'none',
            fontWeight: 600,
            borderRadius: '8px',
            px: 3,
          }}
        >
          Cancel
        </Button>

        <Button
          onClick={handleAssign}
          variant="contained"
          disabled={selectedSMEs.length === 0 || loading || assigning}
          sx={{
            textTransform: 'none',
            fontWeight: 600,
            borderRadius: '8px',
            px: 3,
            minWidth: 140,
            backgroundColor: COLORS.PRIMARY,
            '&:hover': {
              backgroundColor: '#1565c0',
            },
          }}
        >
          {assigning ? (
            <>
              <CircularProgress size={16} sx={{ mr: 1, color: 'white' }} />
              {currentlyAssignedSMEs.length > 0 ? 'Reassigning...' : 'Assigning...'}
            </>
          ) : (
            `${currentlyAssignedSMEs.length > 0 ? 'Reassign' : 'Assign'} ${selectedCount > 0 ? `${selectedCount} SME${selectedCount > 1 ? 's' : ''}` : 'SME'}`
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AssignSMEDialog;